import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MailOpen, Trash2, Calendar, User, MessageSquare, Paperclip, Download } from 'lucide-react';
import api from '../services/api';

interface FileAttachment {
  name: string;
  url: string;
  size: number;
  content_type: string;
}

interface ContactMessage {
  id: number;
  name: string;
  email: string;
  subject: string;
  message: string;
  files: FileAttachment[];
  is_read: boolean;
  created_at: string;
  replied_at: string | null;
}

const MessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const response = await api.get('/api/contact/messages/');
      setMessages(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId: number) => {
    try {
      await api.patch(`/api/contact/messages/${messageId}/read/`);
      loadMessages();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    }
  };

  const deleteMessage = async (messageId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      return;
    }

    try {
      await api.delete(`/api/contact/messages/${messageId}/delete/`);
      setSelectedMessage(null);
      loadMessages();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des messages...</p>
        </div>
      </div>
    );
  }

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 transition-all duration-300 border border-blue-200/50"
              >
                <ArrowLeft className="w-5 h-5 text-blue-600" />
              </button>

              <div>
                <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  Messages de Contact
                </h1>
                <p className="text-sm text-gray-600">
                  {unreadCount > 0 ? `${unreadCount} message(s) non lu(s)` : 'Tous les messages sont lus'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Liste des messages */}
          <div className="lg:col-span-1 space-y-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h2 className="font-bold text-gray-900 mb-4">Tous les messages ({messages.length})</h2>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {messages.map((message) => (
                  <button
                    key={message.id}
                    onClick={() => {
                      setSelectedMessage(message);
                      if (!message.is_read) {
                        markAsRead(message.id);
                      }
                    }}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedMessage?.id === message.id
                        ? 'border-blue-500 bg-blue-50'
                        : message.is_read
                        ? 'border-gray-200 bg-white hover:border-gray-300'
                        : 'border-amber-200 bg-amber-50 hover:border-amber-300'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {message.is_read ? (
                          <MailOpen className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Mail className="w-4 h-4 text-amber-500" />
                        )}
                        <span className={`text-sm font-semibold ${message.is_read ? 'text-gray-700' : 'text-gray-900'}`}>
                          {message.name}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 truncate mb-1">{message.subject}</p>
                    <p className="text-xs text-gray-500">{formatDate(message.created_at)}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Détails du message */}
          <div className="lg:col-span-2">
            {selectedMessage ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedMessage.subject}</h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4" />
                        <span>{selectedMessage.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${selectedMessage.email}`} className="text-blue-600 hover:underline">
                          {selectedMessage.email}
                        </a>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(selectedMessage.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteMessage(selectedMessage.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-start space-x-3 mb-4">
                    <MessageSquare className="w-5 h-5 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">Message</h4>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedMessage.message}</p>
                    </div>
                  </div>

                  {/* Fichiers joints */}
                  {selectedMessage.files && selectedMessage.files.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                        <Paperclip className="w-5 h-5 text-gray-400" />
                        <span>Fichiers joints ({selectedMessage.files.length})</span>
                      </h4>
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                        {selectedMessage.files.map((file, index) => {
                          const fileUrl = file.url.startsWith('http') ? file.url : `http://localhost:8000${file.url}`;
                          const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
                          const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension);
                          const isPdf = fileExtension === 'pdf';
                          
                          return (
                            <a
                              key={index}
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group relative bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-2 border-2 border-blue-200 hover:border-blue-400 hover:shadow-md transition-all duration-300 cursor-pointer"
                            >
                              <div className="flex flex-col items-center text-center">
                                {/* Icône selon le type de fichier */}
                                <div className="w-12 h-12 mb-2 rounded-md bg-white flex items-center justify-center shadow-sm">
                                  {isImage ? (
                                    <img 
                                      src={fileUrl} 
                                      alt={file.name}
                                      className="w-full h-full object-cover rounded-md"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                      }}
                                    />
                                  ) : null}
                                  <Paperclip className={`w-6 h-6 text-blue-500 ${isImage ? 'hidden' : ''}`} />
                                </div>
                                
                                {/* Nom du fichier */}
                                <p className="text-xs font-medium text-gray-900 truncate w-full mb-1" title={file.name}>
                                  {file.name}
                                </p>
                                
                                {/* Taille et type */}
                                <div className="flex items-center space-x-1 text-xs text-gray-500">
                                  <span className="uppercase font-semibold text-blue-600 text-xs">{fileExtension}</span>
                                </div>
                                
                                {/* Bouton Ouvrir au hover */}
                                <div className="absolute inset-0 bg-blue-600/90 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                  <div className="text-white flex flex-col items-center">
                                    <Download className="w-6 h-6 mb-1" />
                                    <span className="text-xs font-semibold">Ouvrir</span>
                                  </div>
                                </div>
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <a
                      href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                      className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all"
                    >
                      <Mail className="w-5 h-5" />
                      <span>Répondre par email</span>
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Sélectionnez un message pour voir les détails</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
